import { mergeExtras } from '../../services/categoryMatrix';

export const EMPTY = {
  name: '', brand: '', ref: '', slug: '', description: '', name_i18n: {}, description_i18n: {}, seo: null, seo_i18n: {}, refs: [], oem_refs: [], equivalent_ids: [], competitor_urls: [], published: true, tyre: null, wheel: null, attributes: {}, ean: '', condition: 'Neuf', auto_align: false, auto_align_offset: 0.1, type: '', price: '', original_price: '', discount: '', stock: 50, low_stock_threshold: '', packs: [], tiers: [], suppliers: [], sale_multiple: 1, sale_multiple_label: '', weight_kg: '', target_margin_pct: '', primary_supplier_id: null,
  rating: 5, reviews: 0, image: '', images: [], category: 'frein', subcategory: '', extra_categories: [], compatibility: [], compat_review: false, fuel_types: [],
};

export const initialForm = (product, initialName, initialCategory) => (product ? {
  ...EMPTY,
  ...product,
  original_price: product.original_price ?? '',
  stock: product.stock ?? 50,
  low_stock_threshold: product.low_stock_threshold ?? '',
  discount: product.discount ?? '',
  type: product.type || '',
  slug: product.slug || '',
  description: product.description || '',
  name_i18n: product.name_i18n || {},
  description_i18n: product.description_i18n || {},
  seo: product.seo || null,
  seo_i18n: product.seo_i18n || {},
  subcategory: product.subcategory || '',
  extra_categories: (product.extra_categories || []).map(x => ({ category: x.category, subcategory: x.subcategory || '' })),
  refs: product.refs || [],
  oem_refs: product.oem_refs || [],
  ref_brands: product.ref_brands || {},
  equivalent_ids: product.equivalent_ids || [],
  published: product.published !== false,
  tyre: product.tyre || null,
  wheel: product.wheel || null,
  attributes: product.attributes || {},
  ean: product.ean || '',
  condition: product.condition || 'Neuf',
  competitor_urls: product.competitor_urls || [],
  auto_align: !!product.auto_align,
  auto_align_offset: product.auto_align_offset ?? 0.1,
  packs: (product.packs || []).map(p => ({ ...p })),
  tiers: (product.tiers || []).map(t => ({ ...t })),
  sale_multiple: product.sale_multiple || 1,
  sale_multiple_label: product.sale_multiple_label || '',
  weight_kg: product.weight_kg ?? '',
  target_margin_pct: product.target_margin_pct ?? '',
  primary_supplier_id: product.primary_supplier_id || null,
  suppliers: (product.suppliers || []).map(s => ({ ...s, purchase_price: s.purchase_price ?? '' })),
  images: product.images?.length ? product.images : [product.image].filter(Boolean),
  compatibility: (product.compatibility || []).map(r => ({ brand: r.brand, models: r.models || [], fuels: r.fuels || [], engine_names: r.engine_names || [] })),
  compat_sync: product.compat_sync || null,
  compat_review: !!product.compat_review,
  fuel_types: product.fuel_types || [],
  compat_pending: product.compat_pending || null,
} : { ...EMPTY, ...(initialName ? { name: initialName } : {}), ...(initialCategory ? { category: initialCategory.category, subcategory: initialCategory.subcategory || '' } : {}) });

// Returns { error } on validation failure, else { payload }
export const buildPayload = (form, { seo, fitments, manualExtras, autoExtras }) => {
  if (!form.name.trim() || !form.brand.trim() || !form.ref.trim()) return { error: 'Nom, marque et référence sont obligatoires' };
  const price = parseFloat(form.price);
  if (isNaN(price) || price <= 0) return { error: 'Prix invalide' };
  if (form.images.length === 0) return { error: 'Ajoutez au moins une photo (fichier ou URL)' };
  const rules = form.compatibility.filter(r => r.brand);
  if (rules.length !== form.compatibility.length) return { error: 'Chaque règle de compatibilité doit avoir une marque' };
  const seoNote = Math.round((seo.points / seo.max) * 100);
  if (form.published && seoNote < 40 && !window.confirm(`Note SEO ${seoNote}/100 : la fiche sera mal classée par Google. Publier quand même ? (Annuler pour la compléter d'abord)`)) return { cancelled: true };
  const original = form.original_price === '' ? null : parseFloat(form.original_price);
  const packs = form.packs.map(p => ({ id: p.id, label: (p.label || '').trim(), units: parseInt(p.units, 10) || 0, price: parseFloat(p.price) || 0 }));
  if (packs.some(p => !p.label || p.units < 2 || p.price <= 0)) return { error: 'Chaque conditionnement doit avoir un libellé, au moins 2 unités et un prix' };
  const tiers = form.tiers.map(t => ({ min_qty: parseInt(t.min_qty, 10) || 0, discount_pct: parseFloat(t.discount_pct) || 0 })).sort((a, b) => a.min_qty - b.min_qty);
  if (tiers.some(t => t.min_qty < 2 || t.discount_pct <= 0 || t.discount_pct > 90)) return { error: 'Chaque palier dégressif doit avoir une quantité ≥ 2 et une remise entre 1 et 90 %' };
  if (tiers.some((t, i) => i > 0 && (t.min_qty === tiers[i - 1].min_qty || t.discount_pct <= tiers[i - 1].discount_pct))) return { error: 'Les paliers doivent avoir des quantités différentes et une remise croissante' };
  const payload = {
    name: form.name.trim(),
    brand: form.brand.trim().toUpperCase(),
    ref: form.ref.trim(),
    slug: (form.slug || '').trim() || null,
    description: (form.description || '').trim(),
    name_i18n: form.name_i18n || {},
    description_i18n: form.description_i18n || {},
    seo: form.seo && Object.values(form.seo).some(x => (Array.isArray(x) ? x.length : x)) ? { ...form.seo, faq: (form.seo.faq || []).filter(q => q.question && q.answer) } : null,
    seo_i18n: form.seo_i18n || {},
    refs: form.refs,
    oem_refs: form.oem_refs,
    ref_brands: form.ref_brands || {},
    equivalent_ids: form.equivalent_ids,
    published: !!form.published,
    tyre: form.tyre || null,
    wheel: form.wheel || null,
    attributes: Object.fromEntries(Object.entries(form.attributes || {}).map(([k, v]) => [k, (v || '').trim()]).filter(([, v]) => v)),
    ean: (form.ean || '').trim(),
    condition: form.condition || 'Neuf',
    competitor_urls: form.competitor_urls.map(u => (u || '').trim()).filter(Boolean),
    auto_align: !!form.auto_align,
    auto_align_offset: Math.max(0, parseFloat(form.auto_align_offset) || 0),
    type: form.type.trim() || null,
    price,
    stock: Math.max(0, parseInt(form.stock, 10) || 0),
    low_stock_threshold: form.low_stock_threshold === '' || form.low_stock_threshold == null ? null : Math.max(0, parseInt(form.low_stock_threshold, 10) || 0),
    packs,
    tiers,
    sale_multiple: Math.max(1, parseInt(form.sale_multiple, 10) || 1),
    sale_multiple_label: (form.sale_multiple_label || '').trim(),
    weight_kg: form.weight_kg === '' || form.weight_kg == null ? null : parseFloat(String(form.weight_kg).replace(',', '.')),
    target_margin_pct: form.target_margin_pct === '' || form.target_margin_pct == null ? null : parseFloat(form.target_margin_pct),
    primary_supplier_id: form.suppliers.some(s => s.supplier_id === form.primary_supplier_id) ? form.primary_supplier_id : null,
    suppliers: form.suppliers.map(s => ({ supplier_id: s.supplier_id, supplier_ref: (s.supplier_ref || '').trim(), purchase_price: s.purchase_price === '' || s.purchase_price == null ? null : parseFloat(s.purchase_price), stock: Math.max(0, parseInt(s.stock, 10) || 0) })),
    original_price: original,
    discount: form.discount === '' ? (original && original > price ? Math.round((1 - price / original) * 100) : null) : parseInt(form.discount),
    rating: parseFloat(form.rating) || 0,
    reviews: parseInt(form.reviews) || 0,
    image: form.images[0],
    images: form.images,
    category: form.category,
    subcategory: form.subcategory || null,
    extra_categories: mergeExtras(manualExtras, autoExtras)
      .filter(x => x.category && x.category !== form.category)
      .filter((x, i, arr) => arr.findIndex(y => y.category === x.category && (y.subcategory || '') === (x.subcategory || '')) === i)
      .map(x => ({ category: x.category, subcategory: x.subcategory || null })),
    compatibility: rules,
    compat_review: !!form.compat_review,
    fuel_types: form.fuel_types || [],
    fitments: fitments || undefined,
  };
  return { payload };
};
